import { useEffect, useMemo, useReducer, useRef } from "react";

import { probeVideoDurationSeconds } from "~/shared/lib/video-duration";
import type { PendingUpload } from "~/shared/store/types";

export interface InputVideoDuration {
    /** Суммарная длина входных видео; `null` — измерены не все (или видео нет). */
    readonly seconds: number | null;
    /** Видео есть, но длина ещё не известна — показанная цена НЕПОЛНАЯ. */
    readonly pending: boolean;
}

/**
 * Суммарная длина приложенных видео в секундах — то, что цена обязана знать.
 *
 * Сервер суммирует входные видео так же (probe по всем ссылкам сразу), поэтому
 * котировка отдаёт одну сумму, а не список. Пока измерены не все — `seconds`
 * пуст, а `pending` поднят: цена в этот момент посчитана БЕЗ входа, и отправлять
 * по ней нельзя — спишется больше показанного. Файл с диска меряется мгновенно,
 * а выбранный из библиотеки тянет метаданные с хранилища и может занять
 * десятки секунд — именно в это окно и попадал бы клик.
 *
 * Измеренное кешируется по ссылке: пересчёт цены при смене разрешения или
 * длительности не должен заново читать метаданные тех же файлов.
 */
export function useInputVideoDurationSeconds(items: readonly PendingUpload[]): InputVideoDuration {
    const urls = useMemo(
        () =>
            items
                .filter((item) => item.kind === "video")
                .map((item) => item.previewUrl)
                .filter((url): url is string => !!url),
        [items],
    );
    const urlsKey = urls.join("|");

    const measuredRef = useRef(new Map<string, number>());
    const [, onMeasured] = useReducer((tick: number) => tick + 1, 0);

    useEffect(() => {
        const pending = urls.filter((url) => !measuredRef.current.has(url));
        if (pending.length === 0) return;

        let cancelled = false;
        void Promise.all(
            pending.map(async (url) => {
                const seconds = await probeVideoDurationSeconds(url);
                if (seconds !== null) measuredRef.current.set(url, seconds);
            }),
        ).then(() => {
            if (!cancelled) onMeasured();
        });

        return () => {
            cancelled = true;
        };
        // urlsKey — стабильный слепок набора ссылок: сам массив пересоздаётся
        // на каждое изменение очереди вложений, и эффект бы перезапускался зря.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlsKey]);

    if (urls.length === 0) return { seconds: null, pending: false };

    let total = 0;
    for (const url of urls) {
        const seconds = measuredRef.current.get(url);
        if (seconds === undefined) return { seconds: null, pending: true };
        total += seconds;
    }
    return { seconds: total, pending: false };
}
